import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CT } from "@/lib/connectionsTheme";

const STYLE_OPTIONS = [
  { value: "auto", label: "Auto (branded if a logo exists)" },
  { value: "standard", label: "Standard" },
  { value: "branded", label: "Branded (with your logo)" },
];

export const QRCodeCreateModal = ({ models, fixedModelId, onClose, onCreated }) => {
  const { API } = useAuth();
  const [modelId, setModelId] = useState(fixedModelId || models[0]?.model_id || "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!modelId) {
      setError("Select an Intake Model");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/org/intake/models/${modelId}/qr-codes`,
        { name: name.trim(), description: description.trim() || null, style },
        { withCredentials: true }
      );
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create QR code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border border-slate-200 bg-white text-slate-900 dark:border-blue-500/20 dark:bg-[#0F172A] dark:text-white sm:max-w-md" data-testid="qr-create-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-white">Generate QR Code</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {!fixedModelId && (
            <div>
              <label className={CT.label}>Intake Model</label>
              <select value={modelId} onChange={(e) => setModelId(e.target.value)} data-testid="qr-create-model-select" className={`${CT.input} mt-1`}>
                {models.map((m) => (
                  <option key={m.model_id} value={m.model_id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={CT.label}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front Window QR" data-testid="qr-create-name-input" className={`${CT.input} mt-1`} />
          </div>
          <div>
            <label className={CT.label}>Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="qr-create-description-input" className={`${CT.input} mt-1`} />
          </div>
          <div>
            <label className={CT.label}>Style</label>
            <select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="qr-create-style-select" className={`${CT.input} mt-1`}>
              {STYLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="text-xs font-semibold text-red-600 dark:text-red-400" data-testid="qr-create-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="qr-create-cancel-btn" className={CT.btnSecondary}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="qr-create-submit-btn" className={CT.btnPrimary}>
            {busy && <Loader2 size={14} className="animate-spin" />} Generate
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
